import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import {
  Settings,
  Mail,
  Server,
  Lock,
  Send,
  Save,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Plus,
  X,
  Clock,
  Play,
  History
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const SettingsPage = () => {
  const [emailSettings, setEmailSettings] = useState({
    enabled: false,
    smtp_host: "",
    smtp_port: 587,
    smtp_user: "",
    smtp_password: "",
    smtp_ssl: true,
    sender_email: "",
    recipient_emails: []
  });
  const [cronSettings, setCronSettings] = useState({
    daily_report_enabled: true,
    daily_report_time: "08:00",
    expiry_alert_enabled: true,
    expiry_alert_interval_days: 1,
    secret: "snackstation2024"
  });
  const [cronLogs, setCronLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCron, setSavingCron] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testingCron, setTestingCron] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const [emailRes, cronRes, logsRes] = await Promise.all([
        axios.get(`${API}/settings/email`),
        axios.get(`${API}/cron/settings`),
        axios.get(`${API}/cron/logs?limit=5`)
      ]);
      setEmailSettings(emailRes.data);
      setCronSettings(cronRes.data);
      setCronLogs(logsRes.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Einstellungen");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/email`, emailSettings);
      toast.success("Einstellungen gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/settings/email/test`);
      toast.success("Test-E-Mail gesendet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setTesting(false);
    }
  };

  const handleSaveCron = async () => {
    setSavingCron(true);
    try {
      await axios.put(`${API}/cron/settings`, cronSettings);
      toast.success("Cronjob-Einstellungen gespeichert");
    } catch (err) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSavingCron(false);
    }
  };

  const handleTestCron = async () => {
    setTestingCron(true);
    try {
      const res = await axios.get(`${API}/cron/daily-report?secret=${cronSettings.secret}`);
      toast.success("Tagesbericht versendet!");
      fetchSettings(); // Refresh logs
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setTestingCron(false);
    }
  };

  const addRecipient = () => {
    if (newRecipient && newRecipient.includes("@")) {
      setEmailSettings(prev => ({
        ...prev,
        recipient_emails: [...prev.recipient_emails, newRecipient]
      }));
      setNewRecipient("");
    } else {
      toast.error("Bitte gültige E-Mail-Adresse eingeben");
    }
  };

  const removeRecipient = (email) => {
    setEmailSettings(prev => ({
      ...prev,
      recipient_emails: prev.recipient_emails.filter(e => e !== email)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900 tracking-tight">
          Einstellungen
        </h1>
        <p className="text-slate-500 mt-1">
          E-Mail-Benachrichtigungen konfigurieren
        </p>
      </div>

      {/* Email Settings Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  E-Mail-Benachrichtigungen
                </CardTitle>
                <CardDescription>
                  Erhalten Sie Berichte nach Auffüllungen mit Infos zu ablaufenden Produkten
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor="email-enabled" className="text-sm">Aktiviert</Label>
                <Switch
                  id="email-enabled"
                  checked={emailSettings.enabled}
                  onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, enabled: checked }))}
                  data-testid="email-enabled-switch"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* SMTP Server */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Server className="w-4 h-4" />
                  SMTP-Server
                </Label>
                <Input
                  value={emailSettings.smtp_host}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_host: e.target.value }))}
                  placeholder="z.B. smtp.gmail.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Port</Label>
                <Input
                  type="number"
                  value={emailSettings.smtp_port}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_port: parseInt(e.target.value) || 587 }))}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
            </div>

            {/* Credentials */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Benutzername</Label>
                <Input
                  value={emailSettings.smtp_user}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_user: e.target.value }))}
                  placeholder="user@example.com"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  Passwort
                </Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={emailSettings.smtp_password}
                    onChange={(e) => setEmailSettings(prev => ({ ...prev, smtp_password: e.target.value }))}
                    placeholder="••••••••"
                    data-testid="smtp-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* SSL and Sender */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Absender-Adresse</Label>
                <Input
                  value={emailSettings.sender_email}
                  onChange={(e) => setEmailSettings(prev => ({ ...prev, sender_email: e.target.value }))}
                  placeholder="noreply@example.com"
                  data-testid="sender-email-input"
                />
              </div>
              <div className="flex items-center gap-4 pt-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="smtp-ssl"
                    checked={emailSettings.smtp_ssl}
                    onCheckedChange={(checked) => setEmailSettings(prev => ({ ...prev, smtp_ssl: checked }))}
                    data-testid="smtp-ssl-switch"
                  />
                  <Label htmlFor="smtp-ssl">SSL/TLS verwenden</Label>
                </div>
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label>Empfänger</Label>
              <div className="flex gap-2">
                <Input
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  placeholder="empfaenger@example.com"
                  onKeyPress={(e) => e.key === 'Enter' && addRecipient()}
                  data-testid="recipient-input"
                />
                <Button type="button" onClick={addRecipient} variant="outline" data-testid="add-recipient-btn">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {emailSettings.recipient_emails.map((email, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 rounded-full text-sm"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeRecipient(email)}
                      className="text-slate-400 hover:text-rose-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {emailSettings.recipient_emails.length === 0 && (
                  <span className="text-sm text-slate-400">Keine Empfänger hinzugefügt</span>
                )}
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Hinweis zur E-Mail-Konfiguration</p>
                  <p>Bei Gmail verwenden Sie ein <strong>App-Passwort</strong> statt Ihres normalen Passworts.</p>
                  <p className="mt-1">Typische Ports: 587 (STARTTLS), 465 (SSL), 25 (unverschlüsselt)</p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="save-email-settings-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Speichern..." : "Einstellungen speichern"}
              </Button>
              <Button
                onClick={handleTestEmail}
                disabled={testing || !emailSettings.enabled || !emailSettings.smtp_host}
                variant="outline"
                data-testid="test-email-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {testing ? "Senden..." : "Test-E-Mail senden"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Notification Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Was wird benachrichtigt?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-emerald-500 rounded-full mt-1.5" />
                <span><strong>Nach jeder Auffüllung:</strong> Welches Produkt wurde aufgefüllt (vorher/nachher)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full mt-1.5" />
                <span><strong>Ablaufende Produkte:</strong> Produkte die innerhalb von 30 Tagen ablaufen</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-rose-500 rounded-full mt-1.5" />
                <span><strong>Niedriger Bestand:</strong> Produkte mit unterdurchschnittlichem Bestand</span>
              </li>
            </ul>
          </CardContent>
        </Card>
      </motion.div>

      {/* Cronjob Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <Card className="border-slate-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Automatische Berichte (Cronjobs)
                </CardTitle>
                <CardDescription>
                  Tägliche Berichte per E-Mail erhalten
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Daily Report */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
              <div>
                <div className="font-medium text-sm">Täglicher Bericht</div>
                <div className="text-xs text-slate-500">Zusammenfassung: MHD, Bestand, Probleme</div>
              </div>
              <Switch
                checked={cronSettings.daily_report_enabled}
                onCheckedChange={(checked) => setCronSettings(prev => ({ ...prev, daily_report_enabled: checked }))}
                data-testid="daily-report-switch"
              />
            </div>

            {/* Expiry Alert */}
            <div className="p-3 bg-slate-50 rounded-md space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">Ablauf-Warnung</div>
                  <div className="text-xs text-slate-500">Warnung bei abgelaufenen oder bald ablaufenden Produkten</div>
                </div>
                <Switch
                  checked={cronSettings.expiry_alert_enabled}
                  onCheckedChange={(checked) => setCronSettings(prev => ({ ...prev, expiry_alert_enabled: checked }))}
                  data-testid="expiry-alert-switch"
                />
              </div>
              {cronSettings.expiry_alert_enabled && (
                <div className="flex items-center gap-3 pl-1">
                  <Label className="text-xs text-slate-600 whitespace-nowrap">Häufigkeit</Label>
                  <Select
                    value={String(cronSettings.expiry_alert_interval_days ?? 1)}
                    onValueChange={(v) => setCronSettings(prev => ({ ...prev, expiry_alert_interval_days: parseInt(v) }))}
                  >
                    <SelectTrigger className="h-8 w-44 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Täglich</SelectItem>
                      <SelectItem value="3">Alle 3 Tage</SelectItem>
                      <SelectItem value="7">Wöchentlich</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Secret Key */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Secret-Key (für Cronjob-Aufruf)
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showSecret ? "text" : "password"}
                    value={cronSettings.secret}
                    onChange={(e) => setCronSettings(prev => ({ ...prev, secret: e.target.value }))}
                    data-testid="cron-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(!showSecret)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Cronjob URL Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-2">Plesk Cronjob einrichten:</p>
                <code className="block bg-blue-100 p-2 rounded text-xs break-all">
                  curl -s "https://snackomat.luenefire.de/api/cron/daily-report?secret={cronSettings.secret}"
                </code>
                <p className="mt-2 text-xs">Empfohlene Zeit: Täglich um 8:00 Uhr</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                onClick={handleSaveCron}
                disabled={savingCron}
                className="bg-[#DC2626] hover:bg-[#B91C1C]"
                data-testid="save-cron-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingCron ? "Speichern..." : "Speichern"}
              </Button>
              <Button
                onClick={handleTestCron}
                disabled={testingCron || !emailSettings.enabled}
                variant="outline"
                data-testid="test-cron-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {testingCron ? "Senden..." : "Jetzt ausführen"}
              </Button>
            </div>

            {/* Recent Logs */}
            {cronLogs.length > 0 && (
              <div className="pt-4 border-t">
                <Label className="flex items-center gap-2 mb-2">
                  <History className="w-4 h-4" />
                  Letzte Ausführungen
                </Label>
                <div className="space-y-2">
                  {cronLogs.map((log, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">
                        {new Date(log.created_at).toLocaleString("de-DE")}
                      </span>
                      <Badge className={log.status === "success" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}>
                        {log.status === "success" ? "OK" : "Fehler"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default SettingsPage;
